import { useEffect, useRef } from 'react'
import * as maplibregl from 'maplibre-gl'
import type { MapMouseEvent, StyleSpecification } from 'maplibre-gl'
import { useAppStore, type MapStyleId } from '../store/appStore'
import { sunPosition } from '../lib/sun'
import { fetchBuildingsAround } from '../lib/buildings'
import {
  isPointIlluminated,
  shadowsGeoJSON,
  type BuildingFeature,
} from '../lib/shadows'
import {
  buildingsSunGeoJSON,
  frameArrowGeoJSON,
  sunRayGeoJSON,
} from '../lib/geo'

const WARSZAWA: [number, number] = [21.0122, 52.2297]

/**
 * All basemap tiles go through our free proxy — never Carto/Stadia (API key watermarks).
 * street = OSM.de / Esri streets, dark = Esri dark gray, sat = Esri imagery
 */
const TILES_STREET = ['/api/tiles/street/{z}/{x}/{y}.png']
const TILES_DARK = ['/api/tiles/dark/{z}/{x}/{y}.png']
const TILES_SAT = ['/api/tiles/sat/{z}/{x}/{y}.jpg']

function baseStyle(id: MapStyleId): StyleSpecification {
  const tiles =
    id === 'satellite' ? TILES_SAT : id === 'lineart' ? TILES_DARK : TILES_STREET
  const attribution =
    id === 'satellite'
      ? 'Tiles © Esri'
      : id === 'lineart'
        ? 'Tiles © Esri'
        : '© OpenStreetMap'

  return {
    version: 8,
    glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
    sources: {
      basemap: {
        type: 'raster',
        tiles,
        tileSize: 256,
        attribution,
        maxzoom: 19,
      },
      buildings: {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      },
      shadows: {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      },
      'sun-ray': {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      },
      'roof-rays': {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      },
      'frame-arrow': {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      },
    },
    layers: [
      {
        id: 'basemap',
        type: 'raster',
        source: 'basemap',
        paint: { 'raster-opacity': 1, 'raster-fade-duration': 0 },
      },
      {
        id: 'shadows-fill',
        type: 'fill',
        source: 'shadows',
        paint: {
          'fill-color': '#0a1520',
          'fill-opacity': 0.22,
        },
      },
      {
        id: 'sun-cone',
        type: 'fill',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'cone'],
        paint: {
          'fill-color': [
            'interpolate',
            ['linear'],
            ['get', 'warmth'],
            0,
            '#ffe566',
            1,
            '#ff8c42',
          ],
          'fill-opacity': 0.11,
        },
      },
      {
        id: 'sun-core',
        type: 'fill',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'core'],
        paint: {
          'fill-color': [
            'interpolate',
            ['linear'],
            ['get', 'warmth'],
            0,
            '#fff3a0',
            1,
            '#ffb347',
          ],
          'fill-opacity': 0.18,
        },
      },
      {
        id: '3d-buildings',
        type: 'fill-extrusion',
        source: 'buildings',
        minzoom: 13,
        paint: {
          'fill-extrusion-color': [
            'case',
            ['==', ['get', 'lit'], 1],
            '#ffe08a',
            '#c5d0d8',
          ],
          'fill-extrusion-height': ['get', 'height'],
          'fill-extrusion-base': 0,
          'fill-extrusion-opacity': 0.88,
          'fill-extrusion-vertical-gradient': true,
        },
      },
      {
        id: 'roof-rays-line',
        type: 'line',
        source: 'roof-rays',
        filter: ['==', ['get', 'kind'], 'roof-ray'],
        paint: {
          'line-color': '#ffb703',
          'line-width': 2.5,
          'line-opacity': 0.85,
          'line-blur': 0.3,
        },
      },
      {
        id: 'roof-hits',
        type: 'circle',
        source: 'roof-rays',
        filter: ['==', ['get', 'kind'], 'roof-hit'],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 14, 2.5, 17, 5],
          'circle-color': '#ffd60a',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1.2,
          'circle-opacity': 0.9,
        },
      },
      {
        id: 'sun-path-day',
        type: 'line',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'path-day'],
        paint: {
          'line-color': '#f0c978',
          'line-width': 2.5,
          'line-opacity': 0.4,
          'line-dasharray': [1.5, 1.2],
        },
      },
      {
        id: 'sun-path-done',
        type: 'line',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'path-done'],
        paint: {
          'line-color': '#ff9f1c',
          'line-width': 4.5,
          'line-opacity': 0.92,
          'line-blur': 0.15,
        },
      },
      {
        id: 'sun-hour-ticks',
        type: 'line',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'hour-tick'],
        paint: {
          'line-color': '#ffe8a3',
          'line-width': 2,
          'line-opacity': 0.5,
        },
      },
      {
        id: 'sun-elev-guide',
        type: 'line',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'elev-guide'],
        paint: {
          'line-color': '#ffffff',
          'line-width': 1.5,
          'line-opacity': 0.35,
          'line-dasharray': [0.8, 1.2],
        },
      },
      {
        id: 'sun-streaks',
        type: 'line',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'streak'],
        paint: {
          'line-color': '#ffd27a',
          'line-width': 2,
          'line-opacity': 0.4,
          'line-blur': 0.5,
        },
      },
      {
        id: 'sun-ray-halo',
        type: 'line',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'ray'],
        paint: {
          'line-color': '#fff6d0',
          'line-width': 26,
          'line-opacity': 0.45,
          'line-blur': 9,
        },
      },
      {
        id: 'sun-ray-line',
        type: 'line',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'ray'],
        paint: {
          'line-color': [
            'interpolate',
            ['linear'],
            ['get', 'warmth'],
            0,
            '#ff9100',
            1,
            '#ff3d00',
          ],
          'line-width': 6.5,
          'line-opacity': 1,
        },
      },
      {
        id: 'sun-ray-flow',
        type: 'line',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'ray-flow'],
        paint: {
          'line-color': '#ffffff',
          'line-width': 2.5,
          'line-opacity': 0.8,
          'line-dasharray': [0.6, 1.8],
        },
      },
      {
        id: 'sun-arrow',
        type: 'line',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'arrow'],
        paint: {
          'line-color': '#ff3d00',
          'line-width': 6,
          'line-opacity': 1,
        },
      },
      {
        id: 'sun-glow',
        type: 'circle',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'glow'],
        paint: {
          'circle-radius': 38,
          'circle-color': '#ffd60a',
          'circle-opacity': 0.22,
          'circle-blur': 0.9,
        },
      },
      {
        id: 'sun-horizon-dot',
        type: 'circle',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'horizon-dot'],
        paint: {
          'circle-radius': 5,
          'circle-color': '#ffe8a3',
          'circle-stroke-color': '#ff9f1c',
          'circle-stroke-width': 2,
          'circle-opacity': 0.9,
        },
      },
      {
        id: 'sun-disc',
        type: 'circle',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'sun'],
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            14,
            13,
            18,
            20,
          ],
          'circle-color': [
            'interpolate',
            ['linear'],
            ['get', 'warmth'],
            0,
            '#ffe566',
            1,
            '#ff8c42',
          ],
          'circle-stroke-width': 3.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 1,
        },
      },
      {
        id: 'sun-endpoints',
        type: 'circle',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'endpoint'],
        paint: {
          'circle-radius': 7,
          'circle-color': [
            'match',
            ['get', 'role'],
            'rise',
            '#ffb703',
            'set',
            '#fb5607',
            'noon',
            '#ffd60a',
            '#ffd60a',
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.95,
        },
      },
      {
        id: 'sun-endpoint-labels',
        type: 'symbol',
        source: 'sun-ray',
        filter: ['==', ['get', 'kind'], 'endpoint'],
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 11,
          'text-font': ['Noto Sans Bold'],
          'text-offset': [0, 1.35],
          'text-anchor': 'top',
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#fff8e7',
          'text-halo-color': 'rgba(20,16,8,0.75)',
          'text-halo-width': 1.4,
        },
      },
      {
        id: 'frame-arrow-line',
        type: 'line',
        source: 'frame-arrow',
        paint: {
          'line-color': '#4cc9f0',
          'line-width': 3.5,
          'line-opacity': 0.95,
        },
      },
    ],
    light: {
      anchor: 'map',
      color: '#ffffff',
      intensity: 0.35,
      position: [1.2, 210, 40],
    },
  }
}

function setData(map: maplibregl.Map, id: string, data: GeoJSON.FeatureCollection) {
  const src = map.getSource(id) as maplibregl.GeoJSONSource | undefined
  src?.setData(data)
}

function updateSunLight(map: maplibregl.Map, azimuthDeg: number, altitudeDeg: number) {
  try {
    const intensity =
      altitudeDeg <= 0 ? 0.22 : Math.min(0.4, 0.28 + Math.max(0, altitudeDeg) / 180)
    map.setLight({
      anchor: 'map',
      position: [1.2, azimuthDeg, Math.max(20, Math.min(50, altitudeDeg + 15))],
      intensity,
      color: altitudeDeg < 8 ? '#ffc078' : '#ffffff',
    })
  } catch {
    /* ignore */
  }
}

export function MapView() {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markerRef = useRef<maplibregl.Marker | null>(null)
  const buildingsRef = useRef<BuildingFeature[]>([])
  const abortRef = useRef<AbortController | null>(null)
  const mapStyleRef = useRef<MapStyleId>('standard')
  const skipFlyRef = useRef(false)

  const pin = useAppStore((s) => s.pin)
  const dateTime = useAppStore((s) => s.dateTime)
  const mapStyle = useAppStore((s) => s.mapStyle)
  const pitchEnabled = useAppStore((s) => s.pitchEnabled)
  const setPin = useAppStore((s) => s.setPin)
  const setIlluminated = useAppStore((s) => s.setIlluminated)
  const setBuildingsLoading = useAppStore((s) => s.setBuildingsLoading)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle('standard'),
      center: pin ? [pin.lng, pin.lat] : WARSZAWA,
      zoom: 16,
      minZoom: 2,
      maxZoom: 20,
      pitch: 55,
      bearing: -20,
      maxPitch: 70,
      attributionControl: false,
      fadeDuration: 0,
      canvasContextAttributes: { antialias: true },
    })

    map.scrollZoom.setWheelZoomRate(1 / 140)
    map.scrollZoom.setZoomRate(1 / 100)
    map.dragRotate.enable()
    map.touchZoomRotate.enable()
    map.doubleClickZoom.enable()
    map.boxZoom.enable()
    map.keyboard.enable()

    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')
    map.addControl(
      new maplibregl.NavigationControl({ visualizePitch: true, showZoom: true }),
      'bottom-right',
    )
    map.addControl(new maplibregl.ScaleControl({ maxWidth: 140 }), 'bottom-left')

    const onReady = () => {
      map.resize()
      const state = useAppStore.getState()
      if (state.pin) {
        refreshOverlays(
          map,
          state.pin,
          state.dateTime,
          buildingsRef.current,
          setIlluminated,
          state.frame,
        )
      }
    }

    map.on('load', onReady)
    map.on('style.load', onReady)
    map.on('zoomend', () => {
      const state = useAppStore.getState()
      if (!state.pin || !map.isStyleLoaded()) return
      refreshOverlays(
        map,
        state.pin,
        state.dateTime,
        buildingsRef.current,
        setIlluminated,
        state.frame,
      )
    })
    map.on('error', (e) => {
      console.warn('maplibre', e.error ?? e)
    })

    map.on('click', (e: MapMouseEvent) => {
      skipFlyRef.current = true
      setPin({ lng: e.lngLat.lng, lat: e.lngLat.lat })
    })

    // Marching dashes on the incidence beam = light flowing toward pin
    const dashSeq: number[][] = [
      [0, 4, 3],
      [0.5, 4, 2.5],
      [1, 4, 2],
      [1.5, 4, 1.5],
      [2, 4, 1],
      [2.5, 4, 0.5],
      [3, 4, 0],
      [0, 0.5, 3, 3.5],
      [0, 1, 3, 3],
      [0, 1.5, 3, 2.5],
      [0, 2, 3, 2],
      [0, 2.5, 3, 1.5],
      [0, 3, 3, 1],
      [0, 3.5, 3, 0.5],
    ]
    let dashStep = 0
    let raf = 0
    let lastDash = 0
    const tickDash = (ts: number) => {
      raf = requestAnimationFrame(tickDash)
      if (!mapRef.current?.getLayer('sun-ray-flow')) return
      if (ts - lastDash < 45) return
      lastDash = ts
      dashStep = (dashStep + 1) % dashSeq.length
      try {
        map.setPaintProperty('sun-ray-flow', 'line-dasharray', dashSeq[dashStep])
      } catch {
        /* ignore */
      }
    }
    raf = requestAnimationFrame(tickDash)

    const ro = new ResizeObserver(() => map.resize())
    ro.observe(containerRef.current)
    mapRef.current = map
    requestAnimationFrame(() => {
      map.resize()
      map.triggerRepaint()
    })

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      markerRef.current?.remove()
      markerRef.current = null
      map.remove()
      mapRef.current = null
    }
  }, [setPin, setIlluminated])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (mapStyleRef.current === mapStyle) return
    mapStyleRef.current = mapStyle
    map.setStyle(baseStyle(mapStyle))
  }, [mapStyle])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (pitchEnabled) {
      if (map.getPitch() < 25) map.easeTo({ pitch: 55, duration: 400 })
    } else {
      map.easeTo({ pitch: 0, duration: 400 })
    }
  }, [pitchEnabled])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !pin) return

    if (!markerRef.current) {
      const el = document.createElement('div')
      el.style.cssText =
        'width:18px;height:18px;border-radius:50%;background:#f5b942;border:3px solid #fff;box-shadow:0 2px 12px rgba(0,0,0,.5);'
      markerRef.current = new maplibregl.Marker({ element: el })
        .setLngLat([pin.lng, pin.lat])
        .addTo(map)
    } else {
      markerRef.current.setLngLat([pin.lng, pin.lat])
    }

    if (skipFlyRef.current) {
      skipFlyRef.current = false
      return
    }

    const c = map.getCenter()
    const dist = Math.hypot(c.lng - pin.lng, c.lat - pin.lat)
    const z = map.getZoom()
    map.flyTo({
      center: [pin.lng, pin.lat],
      zoom: dist > 0.02 ? Math.max(z, 16) : Math.min(Math.max(z, 15.5), 18),
      pitch: pitchEnabled ? Math.max(map.getPitch(), 50) : 0,
      essential: true,
      duration: dist > 0.05 ? 1200 : 600,
    })
  }, [pin, pitchEnabled])

  useEffect(() => {
    if (!pin) return
    abortRef.current?.abort()
    const ac = new AbortController()
    abortRef.current = ac
    setBuildingsLoading(true)
    fetchBuildingsAround(pin.lat, pin.lng, 350, ac.signal)
      .then((buildings) => {
        buildingsRef.current = buildings
        setBuildingsLoading(false)
        const state = useAppStore.getState()
        const map = mapRef.current
        if (!map?.isStyleLoaded()) return
        refreshOverlays(map, pin, state.dateTime, buildings, setIlluminated, state.frame)
      })
      .catch((err) => {
        if (ac.signal.aborted) return
        console.warn(err)
        buildingsRef.current = []
        setBuildingsLoading(false)
        setIlluminated(null)
      })
    return () => ac.abort()
  }, [pin, setBuildingsLoading, setIlluminated])

  // Keep sun incidence line in sync with CZAS ŚWIATŁA slider / presets / play
  useEffect(() => {
    const applySun = () => {
      const map = mapRef.current
      const state = useAppStore.getState()
      if (!map || !state.pin || !map.isStyleLoaded()) return
      refreshOverlays(
        map,
        state.pin,
        state.dateTime,
        buildingsRef.current,
        setIlluminated,
        state.frame,
      )
    }

    applySun()
    const unsub = useAppStore.subscribe((state, prev) => {
      if (
        state.dateTime.getTime() === prev.dateTime.getTime() &&
        state.frame === prev.frame &&
        state.pin === prev.pin
      ) {
        return
      }
      // Immediate update so scrubbing the time slider rotates the ray live
      applySun()
    })
    return unsub
  }, [setIlluminated])

  // React path: also re-run when dateTime/pin change (belt + suspenders with store.subscribe)
  useEffect(() => {
    const map = mapRef.current
    if (!map || !pin || !map.isStyleLoaded()) return
    const state = useAppStore.getState()
    refreshOverlays(map, pin, dateTime, buildingsRef.current, setIlluminated, state.frame)
  }, [dateTime, pin, setIlluminated])

  const sunHud = pin ? sunPosition(dateTime, pin.lat, pin.lng) : null

  return (
    <div
      id="sunscout-map"
      ref={containerRef}
      className="absolute inset-0 z-0"
      style={{ width: '100%', height: '100%', minHeight: '100dvh', background: '#dce6ec' }}
    >
      {sunHud && (
        <div
          className="pointer-events-none absolute top-24 right-4 z-30 panel-glass rounded-xl px-3 py-2.5 text-xs"
          style={{ minWidth: 148 }}
        >
          <div className="text-[10px] uppercase tracking-wider text-[var(--accent-2)] mb-1.5">
            Padanie światła
          </div>
          <div className="flex items-center gap-3">
            <div
              className="relative w-14 h-14 rounded-full border border-white/20 bg-black/35"
              aria-hidden
            >
              <div className="absolute inset-[3px] rounded-full border border-dashed border-white/15" />
              <div
                className="absolute left-1/2 top-1/2 w-1.5 h-6 origin-bottom rounded-full"
                style={{
                  background:
                    sunHud.altitudeDeg < 12
                      ? 'linear-gradient(#ff8c42,#ff3d00)'
                      : 'linear-gradient(#ffe566,#ff9100)',
                  transform: `translate(-50%, -100%) rotate(${sunHud.azimuthDeg}deg)`,
                  boxShadow: '0 0 10px rgba(255,157,28,0.85)',
                }}
              />
              <div
                className="absolute left-1/2 top-1/2 w-2.5 h-2.5 rounded-full bg-[#ffd60a] border-2 border-white"
                style={{
                  transform: `translate(-50%, -50%) rotate(${sunHud.azimuthDeg}deg) translateY(${
                    -18 + Math.min(12, Math.max(0, sunHud.altitudeDeg) * 0.18)
                  }px)`,
                }}
              />
              <div className="absolute top-0.5 left-1/2 -translate-x-1/2 text-[8px] text-white/45">
                N
              </div>
            </div>
            <div className="tabular-nums leading-tight">
              <div className="text-base font-semibold text-[var(--accent)]">
                {sunHud.azimuthDeg.toFixed(0)}°
              </div>
              <div className="text-[var(--muted)]">el {sunHud.altitudeDeg.toFixed(1)}°</div>
              <div className="text-[10px] text-white/40 mt-0.5">W→S→Z po łuku</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function bringSunLayersToFront(map: maplibregl.Map) {
  const order = [
    'sun-cone',
    'sun-core',
    'sun-path-day',
    'sun-path-done',
    'sun-hour-ticks',
    'sun-elev-guide',
    'sun-streaks',
    'sun-ray-halo',
    'sun-ray-line',
    'sun-ray-flow',
    'sun-arrow',
    'sun-glow',
    'sun-horizon-dot',
    'sun-disc',
    'sun-endpoints',
    'sun-endpoint-labels',
    'roof-rays-line',
    'roof-hits',
    'frame-arrow-line',
  ]
  for (const id of order) {
    if (map.getLayer(id)) {
      try {
        map.moveLayer(id)
      } catch {
        /* ignore */
      }
    }
  }
}

function refreshOverlays(
  map: maplibregl.Map,
  pin: { lng: number; lat: number },
  dateTime: Date,
  buildings: BuildingFeature[],
  setIlluminated: (v: boolean | null) => void,
  frame: { bearing: number; active: boolean } | null,
) {
  if (!map.isStyleLoaded()) return

  const sun = sunPosition(dateTime, pin.lat, pin.lng)
  updateSunLight(map, sun.azimuthDeg, sun.altitudeDeg)

  const { buildings: bGeo, roofRays } = buildingsSunGeoJSON(
    buildings,
    sun.azimuthDeg,
    sun.altitudeDeg,
  )
  setData(map, 'buildings', bGeo)
  setData(map, 'roof-rays', roofRays)
  setData(map, 'shadows', shadowsGeoJSON(buildings, sun.azimuthDeg, sun.altitudeDeg))

  // Horizon path + incidence beam visible through day + civil twilight
  if (sun.altitudeDeg > -5) {
    const zoom = map.getZoom()
    const lengthM = Math.round(Math.min(1000, Math.max(420, 1500 / Math.pow(2, zoom - 15))))
    setData(
      map,
      'sun-ray',
      sunRayGeoJSON(pin.lng, pin.lat, sun.azimuthDeg, lengthM, sun.altitudeDeg, dateTime),
    )
  } else {
    setData(map, 'sun-ray', { type: 'FeatureCollection', features: [] })
  }

  if (frame?.active) {
    setData(map, 'frame-arrow', frameArrowGeoJSON(pin.lng, pin.lat, frame.bearing))
  } else {
    setData(map, 'frame-arrow', { type: 'FeatureCollection', features: [] })
  }

  bringSunLayersToFront(map)
  map.triggerRepaint()

  if (buildings.length === 0) {
    setIlluminated(sun.altitudeDeg > 0)
  } else if (sun.altitudeDeg <= 0) {
    setIlluminated(false)
  } else {
    setIlluminated(
      isPointIlluminated(pin.lng, pin.lat, buildings, sun.azimuthDeg, sun.altitudeDeg),
    )
  }
}
